// The one rAF loop that owns scroll-driven progress (plan/03 §4). Elements register
// a scroll range; the loop reads each rect once per frame, computes normalized 0..1
// progress, smooths it with a critically-damped spring (halflife 90ms), and writes
// $state. Downstream (the morph scene, DOM parallax) reads via `progress(id)` —
// no scroll handlers scattered in components, no per-component rAF.
//
// Three range modes:
//   'scrub'    — element is TALLER than the viewport (sticky set pieces): 0 when its
//                top reaches the viewport top, 1 when its bottom leaves. The morph.
//   'traverse' — element crosses the viewport: 0 when its top enters at the bottom
//                edge, 1 when its bottom leaves at the top. Parallax, line draws,
//                word fills — anything that should track an element's passage.
//   'page'     — whole-document progress (the nav cut-line). No element needed.
import { damp, clamp01 } from './springs';
import { motionDisabled } from './reduced';

export type RangeMode = 'scrub' | 'traverse' | 'page';

interface Range {
	el: HTMLElement | null;
	mode: RangeMode;
	smooth: number;
}

let counter = 0;

function rawProgress(range: Range): number {
	if (range.mode === 'page') {
		const doc = document.documentElement;
		const total = doc.scrollHeight - window.innerHeight;
		return total > 0 ? clamp01(window.scrollY / total) : 1;
	}
	const rect = range.el!.getBoundingClientRect();
	if (range.mode === 'traverse') {
		return clamp01((window.innerHeight - rect.top) / (window.innerHeight + rect.height));
	}
	// scrub
	const total = rect.height - window.innerHeight;
	return total > 0 ? clamp01(-rect.top / total) : rect.top <= 0 ? 1 : 0;
}

class ScrollEngine {
	#ranges = new Map<string, Range>();
	#raf = 0;
	#running = false;
	#last = 0;
	/** Smoothed 0..1 progress per registered id. */
	values = $state<Record<string, number>>({});

	register(
		el: HTMLElement,
		options: { mode?: RangeMode } = {}
	): { id: string; dispose: () => void } {
		return this.#add({ el, mode: options.mode ?? 'scrub', smooth: 0 });
	}

	/** Whole-page 0..1 progress (scrollY / scrollable height). */
	registerPage(): { id: string; dispose: () => void } {
		return this.#add({ el: null, mode: 'page', smooth: 0 });
	}

	#add(range: Range): { id: string; dispose: () => void } {
		const id = `range-${counter++}`;
		this.#ranges.set(id, range);
		this.values[id] = 0;
		this.#start();
		return {
			id,
			dispose: () => {
				this.#ranges.delete(id);
				const next = { ...this.values };
				delete next[id];
				this.values = next;
				if (this.#ranges.size === 0) this.#stop();
			}
		};
	}

	progress(id: string): number {
		return this.values[id] ?? 0;
	}

	#start() {
		if (this.#running || typeof requestAnimationFrame === 'undefined') return;
		this.#running = true;
		this.#last = performance.now();
		this.#raf = requestAnimationFrame(this.#tick);
	}

	#stop() {
		this.#running = false;
		if (this.#raf) cancelAnimationFrame(this.#raf);
		this.#raf = 0;
	}

	#tick = (now: number) => {
		const dt = Math.min(now - this.#last, 64);
		this.#last = now;
		const reduce = motionDisabled();
		for (const [id, range] of this.#ranges) {
			const raw = rawProgress(range);
			range.smooth = reduce ? raw : damp(range.smooth, raw, 90, dt);
			this.values[id] = range.smooth;
		}
		if (this.#running) this.#raf = requestAnimationFrame(this.#tick);
	};
}

export const scroll = new ScrollEngine();
